import { logger } from '@adornis/base/logging.js';
import { BHDialogFreeFormMails } from '@adornis/bh-mails/client/bh-dialog-free-form-mails.js';
import { BuddyMailAttachment } from '@adornis/bh-matching-mails/entities/attachment.js';
import type { Match } from '@adornis/bh-matching-matches/entities/match.js';
import '@adornis/bh-matching-mails/components/matching-mail-editor.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import type { DialogOptions } from '@adornis/dialog/x-plain-dialog.js';
import { askForFile } from '@adornis/file-utils/client/helper-methods.js';
import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { catchError, map, of } from 'rxjs';

/*
 * @element matching-dialog-matches-free-form-mails
 */
@customElement('matching-dialog-matches-free-form-mails')
export class MatchingDialogMatchesFreeFormMails extends BHDialogFreeFormMails {
  @property({ attribute: false }) protected allMatches?: Match[];

  static override get element_name() {
    return 'matching-dialog-matches-free-form-mails';
  }

  protected async _uploadAttachment() {
    const files = await askForFile({ acceptMultipleFiles: true });
    if (!files?.length) return;
    try {
      const uploadedIDs = await TrDialog.waitFor(
        Promise.all(Array.from(files).map(file => BuddyMailAttachment.upload(file))),
      );
      this._mailTemplate.activatedAttachments = [
        ...(this._mailTemplate.activatedAttachments ?? []),
        ...uploadedIDs,
      ];
      this.requestUpdate();
      void XSnackbar.show('Anhang hochgeladen');
    } catch (err) {
      logger.error({ err });
      void TrDialog.alert('Es gab ein Problem beim Hochladen des Anhangs. Bitte versuchen Sie es später erneut.');
    }
  }

  override getInnerWrapper(options: Required<DialogOptions>) {
    const wrapperElem = super.getInnerWrapper(options);
    wrapperElem.style.setProperty('width', `min(900px, 95vw)`);
    return wrapperElem;
  }

  protected override content() {
    return html`
      <x-flex
        space="lg"
        ${css({
          maxHeight: `calc(80vh - 2 * ${this.spacing.lg})`,
          overflowY: 'auto',
          paddingRight: this.spacing.sm,
        })}
      >
        ${this._mailAddressDisplay()}
        <matching-mail-editor attachments .value=${this._mailTemplate}></matching-mail-editor>
        <x-flex horizontal space-between crossaxis-center space="sm" wrap>
          <x-button mode="outline" icon="upload" @click=${() => this._uploadAttachment()}>
            Neuen Anhang hochladen
          </x-button>
          <x-flex horizontal end space="sm" wrap>
            <x-button mode="outline" @click=${() => (this as any).close()}> Abbrechen </x-button>
            <x-button icon="send" @click=${() => this._sendMails()}> Mail senden </x-button>
          </x-flex>
        </x-flex>
      </x-flex>
    `;
  }

  override async _sendMails() {
    if (!this.mailAddresses?.length) return;

    if (!this.mailSendOperation)
      throw new Error('Cannot happen as this is catched in connectedCallback (bh-dialog-free-form-mails)');
    try {
      const foo = this.mailSendOperation.bind(null)(
        (this.allMatches ?? []).map(m => m._id),
        this._mailTemplate,
      )();

      const bar = (foo as any).pipe(
        map(
          numMailsSent =>
            `${numMailsSent} Mail${(numMailsSent as number) * 1 > 1 ? 's' : ''} wurde${
              (numMailsSent as number) * +1 > 1 ? 'n' : ''
            } bereits versandt.`,
        ),
        // TODO catch this in TrDialog.waitFor and do proper error handling
        catchError(err => {
          logger.error({ err });
          void TrDialog.alert(
            'Es gab ein Problem beim Versenden der Freitext-Mails. Bitte versuchen Sie es später erneut.',
          );
          return of(null);
        }),
      );

      const result = await TrDialog.waitFor<string | null>(bar);
      if (result) void XSnackbar.show('Mails wurden erfolgreich versandt.');
      (this as any).close();
    } catch (err) {
      logger.error({ err });
      void TrDialog.alert(
        'Es gab ein Problem beim Versenden der Freitext-Mails. Bitte versuchen Sie es später erneut.',
      );
    }
  }
}
